import { EventEmitter, h, Component, Prop, Host, Watch, Event, Element } from '@stencil/core';
@Component({
  tag: 'pk-radio-option',
  styleUrl: './scss/pk-radio-option.scss',
  shadow: true,
})
export class PkRadioOption {
  // Own Properties
  // ====================

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkRadioOptionElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  // Public Property API
  // ========================

  /** If true, the radio option will be disabled and no user interaction will be allowed */
  @Prop({ reflect: true }) isDisabled: boolean;

  /** If true, the radio option will be set as checked */
  @Prop({ reflect: true, mutable: true }) checked: boolean = false;

  /** Set radio name */
  @Prop({ reflect: true }) name: string;

  /** Set radio id */
  @Prop({ reflect: true }) uid: string;

  /** Set radio value */
  @Prop({ reflect: true }) value: string;

  // Prop lifecycle events
  // =======================
  @Watch('checked')
  watchChecked() {
    if (!this.checked) return;

    const otherRadiosInGroup = this.getSiblingRadios();
    if (!otherRadiosInGroup) return;

    otherRadiosInGroup.forEach((radio) => {
      radio.checked = false;
    });
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Callback handler to be called when the radio option checked value change */
  @Event() pkChange: EventEmitter<{ selectedValue: string; checked: boolean }>;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  getAllRadios() {
    const radioGroup = this.el.closest('pk-radio');
    // Radios must be part of a radio group
    if (radioGroup === null) return;
    // Will return all the radio options within the sanem name (under the same <pk-radio>)
    return Array.from(radioGroup.querySelectorAll('pk-radio-option')).filter((radio) => radio.name === this.name);
  }

  getSiblingRadios() {
    const radioElements = this.getAllRadios();
    if (!radioElements) return;

    return radioElements.filter((radio) => radio !== this.el);
  }

  private handleClick = () => {
    this.checked = true;
    this.pkChange.emit({ selectedValue: this.value, checked: this.checked });
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    return (
      <Host
        class={{
          'is-disabled': this.isDisabled,
        }}
        aria-disabled={this.isDisabled ? 'true' : 'false'}
        aria-checked={this.checked ? 'true' : 'false'}
        role="radio"
      >
        <label
          class="flex h-[var(--radio--box-height)] cursor-pointer items-center leading-[var(--radio--box-height)]"
          htmlFor={this.uid}
          part="label"
        >
          <input
            class="pk-radio"
            name={this.name}
            title={this.name}
            type="radio"
            id={this.uid}
            value={this.value}
            checked={this.checked}
            disabled={this.isDisabled}
            onClick={this.handleClick}
            part="input"
          />
          <slot />
          {/* to fix jsx-a11y/label-has-associated-control */}
        </label>
      </Host>
    );
  }
}
